// ─── Portfolio Data Parser & Sanitizer ──────────────────────────────
// Robustly parses freelancer_reviews.json, handling missing values,
// empty arrays, or string types gracefully to prevent NaN errors.

export interface ProjectReview {
  role: string;
  clientName: string;
  clientUsername: string;
  rating: number;
  text: string;
  onTime: string;
  onBudget: string;
  categoryRatings: Record<string, number>;
}

export interface ProjectDates {
  posted: string;
  awarded: string;
  completed: string | null;
  postedDisplay: string;
  month: number;
  year: number;
}

export interface Project {
  id: string;
  url: string;
  title: string;
  category: string;
  description: string;
  skills: string[];
  budget: string;
  currency: string;
  client: {
    name: string;
    username: string;
    id: string;
  };
  bid: {
    bidId: string;
    bidAmount: number;
    bidStatus: string;
    milestoneAmount: number;
    milestoneCount: number;
    recruiter: boolean;
    timeAccepted: string;
    closeTime: string;
  };
  dates: ProjectDates;
  review: ProjectReview | null;
}

/**
 * Safely parse a rating value, returning 0 for any non-numeric input.
 */
export function safeParseRating(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const num = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(num) ? Math.max(0, Math.min(5, num)) : 0;
}

/**
 * Sanitize a single review object, ensuring all numeric fields are valid.
 */
function sanitizeReview(review: any): ProjectReview | null {
  if (!review || typeof review !== "object") return null;

  const categoryRatings: Record<string, number> = {};
  if (review.categoryRatings && typeof review.categoryRatings === "object") {
    for (const [key, val] of Object.entries(review.categoryRatings)) {
      categoryRatings[key] = safeParseRating(val);
    }
  }

  return {
    role: String(review.role || ""),
    clientName: String(review.clientName || ""),
    clientUsername: String(review.clientUsername || ""),
    rating: safeParseRating(review.rating),
    text: String(review.text || ""),
    onTime: String(review.onTime || ""),
    onBudget: String(review.onBudget || ""),
    categoryRatings,
  };
}

/**
 * Sanitize an array of project objects from the raw JSON data.
 */
export function sanitizeProjects(rawData: any[]): Project[] {
  if (!Array.isArray(rawData)) return [];

  return rawData
    .filter((item) => item && typeof item === "object" && item.id)
    .map((item) => ({
      id: String(item.id),
      url: String(item.url || ""),
      title: String(item.title || "Untitled Project"),
      category: String(item.category || ""),
      description: String(item.description || ""),
      skills: Array.isArray(item.skills)
        ? item.skills.map(String)
        : [],
      budget: String(item.budget || ""),
      currency: String(item.currency || "USD"),
      client: {
        name: String(item.client?.name || ""),
        username: String(item.client?.username || ""),
        id: String(item.client?.id || ""),
      },
      bid: {
        bidId: String(item.bid?.bidId || ""),
        bidAmount: Number(item.bid?.bidAmount) || 0,
        bidStatus: String(item.bid?.bidStatus || ""),
        milestoneAmount: Number(item.bid?.milestoneAmount) || 0,
        milestoneCount: Number(item.bid?.milestoneCount) || 0,
        recruiter: Boolean(item.bid?.recruiter),
        timeAccepted: String(item.bid?.timeAccepted || ""),
        closeTime: String(item.bid?.closeTime || ""),
      },
      dates: {
        posted: String(item.dates?.posted || ""),
        awarded: String(item.dates?.awarded || ""),
        completed: item.dates?.completed
          ? String(item.dates.completed)
          : null,
        postedDisplay: String(item.dates?.postedDisplay || ""),
        month: Number(item.dates?.month) || 1,
        year: Number(item.dates?.year) || 2012,
      },
      review: sanitizeReview(item.review),
    }));
}

/**
 * Calculate the average rating from an array of projects.
 * Returns "0.0" for empty arrays or projects with no reviews.
 */
export function calculateAverageRating(projects: Project[]): string {
  if (!Array.isArray(projects) || projects.length === 0) return "0.0";

  const reviewed = projects.filter(
    (p) => p.review && p.review.rating > 0
  );
  if (reviewed.length === 0) return "0.0";

  const sum = reviewed.reduce((acc, p) => acc + (p.review?.rating || 0), 0);
  const avg = sum / reviewed.length;

  return Number.isFinite(avg) ? avg.toFixed(1) : "0.0";
}
